/**
 * @file useNavCounters.ts
 * @description Счётчики меню кабинета магазина (phase19.31, SC18, SC41).
 *
 * Один запрос на все счётчики (`GET /admin/nav-counters`) вместо хука на
 * каждый: при открытии кабинета, раз в 60 секунд, пока вкладка видна, и при
 * возврате на вкладку (но не чаще, чем раз в `RETURN_MIN_GAP_MS`). Ошибка —
 * меню просто без счётчиков: необязательное число не должно ронять меню.
 * Смена магазина вкладки (`contextKey`) — прежние счётчики сбрасываются сразу
 * и запрос уходит заново.
 */

import { useEffect, useState } from "react";

import { getNavCounters } from "../lib/api-nav-counters";
import type { NavCounters } from "../lib/shop-nav";

/** Период опроса. */
export const NAV_COUNTERS_POLL_MS = 60_000;
/** Возврат на вкладку не чаще — частые переключения не должны засыпать сервер. */
const RETURN_MIN_GAP_MS = 10_000;

export function useNavCounters(enabled: boolean, contextKey: string | null): NavCounters {
  const [counters, setCounters] = useState<NavCounters>({});

  useEffect(() => {
    // Другой магазин вкладки (или меню выключено) — числа прежнего магазина
    // убираем сразу, не дожидаясь ответа: иначе они висели бы у чужих пунктов.
    setCounters({});
    if (!enabled) return;
    let alive = true;
    let lastFetch = 0;

    const load = () => {
      lastFetch = Date.now();
      getNavCounters()
        .then((value) => {
          if (alive) setCounters(value ?? {});
        })
        .catch(() => {
          if (alive) setCounters({});
        });
    };

    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, NAV_COUNTERS_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible" && Date.now() - lastFetch >= RETURN_MIN_GAP_MS) load();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, contextKey]);

  return counters;
}
